import { useMemo, Fragment } from "react"
import { Box, List, useTheme } from "@mui/material"

import { SKELETON_COUNT, SKELETON_INDEX_TO_REF } from "constant"
import { getIcon } from "utils/icon"

import { useTranslation } from "providers/i18n"

import { EmptyData } from "components/EmptyData"
import { HorizontalScrollPane } from "components/HorizontalScrollPane"
import { Head } from "components/Head"
import { StoreIcon } from "components/icons"
import {
  CategoryFilterBar,
  CategoryFilterChip,
} from "components/CategoryFilterBar"
import { FeatureSkeletonListItem } from "components/domains/features"
import { VenueFilterBar } from "extensions/iconsiam/components/VenueFilterBar"
import { PageLayout } from "extensions/iconsiam/layouts/PageLayout"
import { Header } from "extensions/iconsiam/components/Header"
import { HeaderSearchButton } from "extensions/iconsiam/components/Header/HeaderSearchButton"
import ShopListItem from "extensions/iconsiam/components/domains/directory/ShopListItem"
import { useDirectory } from "extensions/iconsiam/hooks/directory/useDirectory"

export const Directory = ({ Layout = PageLayout, AppNavigation }) => {
  const { t } = useTranslation()

  const {
    handleChangeParentCategory,
    handleChangeSubCategory,
    changeVenueFilter,
    filteredOccupantsSorted,
    sortedParentCategoryWithOccupant,
    subCategoryWithActiveParentCategory,
    parentCategoryId,
    subCategoryId,
    venueFilter,
    ref,
    endScrollRef,
    hasNextPage,
    showSubCategoryFilter,
    isMultiOrdinalVenue,
    hasData,
  } = useDirectory()
  const theme = useTheme()
  const emptyIcon = useMemo(
    () => getIcon(theme?.assets?.empty?.directory) || <StoreIcon />,
    [theme]
  )
  const styleOverrides = theme.components[`DirectoryPage`]?.styleOverrides || {}
  const {
    root,
    filterSection,
    scrollPane,
    categoryFilterBar,
    subCategoryFilterBar,
    list,
    emptyDataWrapper,
  } = styleOverrides

  return (
    <>
      <Head title={t("Directory")} />
      <Layout
        header={
          <Header
            filterBar={
              <VenueFilterBar
                onClickVenue={changeVenueFilter}
                value={venueFilter}
              />
            }
            searchButton={<HeaderSearchButton />}
          />
        }
      >
        <Box ref={ref} sx={root}>
          {
            //use hasData || sortedParentCategoryWithOccupant to avoid componnent disappear during changing category
            (hasData || sortedParentCategoryWithOccupant) && (
              <Box sx={filterSection}>
                <HorizontalScrollPane
                  className="category-filterbar"
                  stackProps={{
                    sx: scrollPane,
                  }}
                >
                  <CategoryFilterBar
                    sx={categoryFilterBar}
                    activeCategory={parentCategoryId}
                    categories={sortedParentCategoryWithOccupant}
                    onChange={handleChangeParentCategory}
                  />
                </HorizontalScrollPane>
                {showSubCategoryFilter && (
                  <HorizontalScrollPane
                    className="subCategory-filterbar"
                    stackProps={{
                      sx: scrollPane,
                    }}
                  >
                    <CategoryFilterBar
                      sx={subCategoryFilterBar}
                      activeCategory={subCategoryId}
                      categories={subCategoryWithActiveParentCategory}
                      onChange={handleChangeSubCategory}
                      renderFilterButton={CategoryFilterChip}
                    />
                  </HorizontalScrollPane>
                )}
              </Box>
            )
          }
          <List sx={list}>
            {hasData ? (
              <>
                {filteredOccupantsSorted.pages.map((page, pageNum) => {
                  return (
                    <Fragment key={pageNum}>
                      {page.data.map((occupant, index) => (
                        // TODO:: Render collapse list item when hasMultipleLocations
                        <ShopListItem
                          id={occupant?.id}
                          data-testid={occupant?.id}
                          key={`${
                            occupant?.properties.isAd ? "ad-" : "directory-item"
                          }${occupant.id}`}
                          occupant={occupant}
                          hideLevel={!isMultiOrdinalVenue}
                        />
                      ))}
                    </Fragment>
                  )
                })}
                {hasNextPage &&
                  Array.from({ length: SKELETON_COUNT }).map((_, index) => (
                    <FeatureSkeletonListItem
                      ref={
                        index === SKELETON_INDEX_TO_REF ? endScrollRef : null
                      }
                      key={index}
                      divider
                    />
                  ))}
              </>
            ) : (
              <Box sx={emptyDataWrapper}>
                <EmptyData
                  icon={emptyIcon}
                  primary={t("Coming soon")}
                  secondary={t("Stay tuned for the update")}
                />
              </Box>
            )}
          </List>
        </Box>
      </Layout>
    </>
  )
}
